import React, { useState, useEffect, useContext, useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import isoWeek from 'dayjs/plugin/isoWeek';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import 'dayjs/locale/pl';
import 'dayjs/locale/en';
import axios from '../../../axiosConfig';

import OwnerLayout from '../OwnerLayout';
import { OwnerContext } from '../../../context/OwnerContext';

import {
    Calendar,
    CalendarEvent,
    useCalendarNavigation,
    useCalendarSelection,
    useResponsiveCalendar,
} from '../../calendar';
import '../../calendar/styles/index.css';

import AdditionalInfoModal from './AdditionalInfoModal';
import DeleteConfirmationModal from '../../trainer/DeleteConfirmationModal';

import '../../../styles/OwnerCalendar.css';
import '../../../styles/OwnerCalendarBookingBar.css';

dayjs.extend(isoWeek);
dayjs.extend(isSameOrAfter);
dayjs.extend(isSameOrBefore);

const DAY_OF_WEEK_MAPPING = {
    1: 'MONDAY',
    2: 'TUESDAY',
    3: 'WEDNESDAY',
    4: 'THURSDAY',
    5: 'FRIDAY',
    6: 'SATURDAY',
    7: 'SUNDAY',
};

const formatDurationForDisplay = (duration) => {
    if (!duration) return '60 min';
    if (typeof duration === 'number') return `${duration} min`;

    const match = duration.match(/PT(?:(\d+)H)?(?:(\d+)M)?/);
    if (!match) return '60 min';

    const hours = parseInt(match[1] || '0', 10);
    const minutes = parseInt(match[2] || '0', 10);
    const totalMinutes = hours * 60 + minutes;

    if (hours > 0 && minutes > 0) {
        return `${hours}h ${minutes}min`;
    } else if (hours > 0) {
        return `${hours}h`;
    }
    return `${totalMinutes} min`;
};

const OwnerCalendarControls = ({
                                   trainers,
                                   ownerDogs,
                                   services,
                                   selectedTrainerId,
                                   selectedDogId,
                                   selectedServiceId,
                                   onTrainerChange,
                                   onDogChange,
                                   onServiceChange,
                                   onMultipleBookingClick,
                                   bookingMode,
                               }) => {
    const { t } = useTranslation('calendar');
    return (
        <div className="owner-calendar-controls">
            <div className="owner-controls-row">
                <div className="owner-control-item">
                    <select
                        className={`owner-dropdown ${selectedTrainerId ? 'has-value' : ''}`}
                        value={selectedTrainerId || ''}
                        onChange={(e) => onTrainerChange(e.target.value)}
                    >
                        <option value="">{t('selectTrainer')}</option>
                        {trainers.map((trainer) => (
                            <option key={trainer.id} value={trainer.id}>
                                {trainer.firstName} {trainer.lastName}
                            </option>
                        ))}
                    </select>
                </div>

                <div className="owner-control-item">
                    <select
                        className={`owner-dropdown ${selectedDogId ? 'has-value' : ''}`}
                        value={selectedDogId || ''}
                        onChange={(e) => onDogChange(e.target.value)}
                    >
                        <option value="">{t('selectDog')}</option>
                        {ownerDogs.map((dog) => (
                            <option key={dog.id} value={dog.id}>
                                {dog.name}
                            </option>
                        ))}
                    </select>
                </div>

                <div className="owner-control-item">
                    <select
                        className={`owner-dropdown ${selectedServiceId ? 'has-value' : ''}`}
                        value={selectedServiceId || ''}
                        onChange={(e) => onServiceChange(e.target.value)}
                    >
                        <option value="">{t('selectTrainingType')}</option>
                        {services.map((service) => (
                            <option key={service.id} value={service.id}>
                                {service.serviceName} ({formatDurationForDisplay(service.calendarDuration)})
                            </option>
                        ))}
                    </select>
                </div>
            </div>
        </div>
    );
};

const SelectionPlaceholder = ({ isMobileView = false }) => {
    const { t } = useTranslation('calendar');
    return (
        <div className={isMobileView ? 'owner-mobile-placeholder' : 'owner-calendar-placeholder'}>
            <div className={isMobileView ? 'owner-mobile-no-selection' : 'owner-no-selection'}>
                <div className={isMobileView ? 'owner-mobile-no-selection-icon' : 'owner-no-selection-icon'}>
                    📅
                </div>
                <div className={isMobileView ? 'owner-mobile-no-selection-text' : 'owner-no-selection-text'}>
                    {t('selectAllToShowCalendar')}
                </div>
            </div>
        </div>
    );
};

const BookingBar = ({
                        selectedSlots,
                        onClearSelection,
                        onBookSlots,
                        isBooking,
                    }) => {
    const { t } = useTranslation('calendar');
    if (selectedSlots.length === 0) return null;

    return (
        <div className="owner-booking-bar">
            <div className="owner-booking-bar__info">
        <span className="owner-booking-bar__count">
          {t('slotsSelected')} <strong>{selectedSlots.length}</strong>
        </span>
            </div>
            <div className="owner-booking-bar__actions">
                <button
                    className="owner-booking-bar__clear"
                    onClick={onClearSelection}
                    disabled={isBooking}
                >
                    {t('clearSelection')}
                </button>
                <button
                    className="owner-booking-bar__submit"
                    onClick={onBookSlots}
                    disabled={isBooking}
                >
                    {isBooking ? t('booking') : `${t('book')} (${selectedSlots.length})`}
                </button>
            </div>
        </div>
    );
};

const OwnerCalendarContent = ({
                                  trainings,
                                  trainingLocks,
                                  confirmedTrainings,
                                  trainerGroupTrainings,
                                  dogGroupTrainings,
                                  pendingOtherSlots,
                                  trainers,
                                  ownerDogs,
                                  services,
                                  workingHours,
                                  schoolSettings,
                                  selectedTrainer,
                                  selectedDogId,
                                  selectedServiceId,
                                  serviceDurationMinutes,
                                  onTrainerChange,
                                  onDogChange,
                                  onServiceChange,
                                  onBookSlots,
                                  onCancelTraining,
                                  onWeekChange,
                                  isBooking,
                                  bookingMode,
                                  onToggleBookingMode,
                              }) => {
    const { t, i18n } = useTranslation('calendar');
    const { shouldUseMobileView } = useResponsiveCalendar();
    const { currentWeekStart } = useCalendarNavigation();

    // Set dayjs locale dynamically based on i18n language
    useEffect(() => {
        dayjs.locale(i18n.language);
    }, [i18n.language]);
    const { selectedSlots, toggleSlot, clearSelection, isSlotSelected } = useCalendarSelection();
    const [selectedDay, setSelectedDay] = useState(dayjs());

    // Fetch data when week changes
    useEffect(() => {
        if (selectedTrainer && selectedDogId && selectedServiceId) {
            onWeekChange(currentWeekStart);
        }
    }, [currentWeekStart, selectedTrainer, selectedDogId, selectedServiceId, onWeekChange]);

    // Update selectedDay to stay within current week when navigating
    useEffect(() => {
        const weekEnd = currentWeekStart.add(6, 'day');
        if (selectedDay.isBefore(currentWeekStart, 'day') || selectedDay.isAfter(weekEnd, 'day')) {
            // If selected day is outside current week, reset to first day of week
            setSelectedDay(currentWeekStart);
        }
    }, [currentWeekStart, selectedDay]);

    const isReady = selectedTrainer && selectedDogId && selectedServiceId;

    // Calculate events based on current week
    const events = useMemo(() => {
        if (!selectedTrainer || !workingHours.length) {
            return [];
        }

        const eventsList = [];
        const now = dayjs();

        const DAY_MAPPING = {
            1: 'MONDAY',
            2: 'TUESDAY',
            3: 'WEDNESDAY',
            4: 'THURSDAY',
            5: 'FRIDAY',
            6: 'SATURDAY',
            7: 'SUNDAY',
        };

        const isSlotOccupied = (slotStart, slotEnd) => {
            const start = dayjs(slotStart);
            const end = dayjs(slotEnd);

            const hasTraining = trainings.some((t) => {
                const tStart = dayjs(t.startTime);
                const tEnd = dayjs(t.endTime);
                return start.isBefore(tEnd) && end.isAfter(tStart);
            });
            if (hasTraining) return true;

            const hasConfirmed = confirmedTrainings.some((ct) => {
                const ctStart = dayjs(ct.startTime);
                const ctEnd = dayjs(ct.endTime);
                return start.isBefore(ctEnd) && end.isAfter(ctStart);
            });
            if (hasConfirmed) return true;

            const hasLock = trainingLocks.some((lock) => {
                const lockStart = dayjs(lock.startTime);
                const lockEnd = dayjs(lock.endTime);
                return start.isBefore(lockEnd) && end.isAfter(lockStart);
            });
            if (hasLock) return true;

            // Check if trainer has a group training at this time
            const hasGroupTraining = trainerGroupTrainings.some((gt) => {
                const gtStart = dayjs(gt.startTime);
                const gtEnd = dayjs(gt.endTime);
                return start.isBefore(gtEnd) && end.isAfter(gtStart);
            });
            if (hasGroupTraining) return true;

            // Check if there are pending requests from other users/dogs (only block when allowReserveList is false)
            if (schoolSettings?.allowReserveList === false) {
                const hasPendingOther = pendingOtherSlots.some((ps) => {
                    const psStart = dayjs(ps.startTime);
                    const psEnd = dayjs(ps.endTime);
                    return start.isBefore(psEnd) && end.isAfter(psStart);
                });
                if (hasPendingOther) return true;
            }

            return false;
        };

        // Helper function to count pending requests for a time slot
        const getPendingCountForSlot = (slotStart, slotEnd) => {
            const start = dayjs(slotStart);
            const end = dayjs(slotEnd);
            return pendingOtherSlots.filter((ps) => {
                const psStart = dayjs(ps.startTime);
                const psEnd = dayjs(ps.endTime);
                return start.isBefore(psEnd) && end.isAfter(psStart);
            }).length;
        };

        // Add owner's trainings
        trainings.forEach((training) => {
            const isConfirmed = training.confirmed;
            const canCancel = dayjs(training.startTime).isAfter(now);

            eventsList.push({
                id: training.id,
                slotId: `training-${training.id}`,
                startTime: training.startTime,
                endTime: training.endTime,
                type: isConfirmed ? 'owner-confirmed' : 'owner-pending',
                isOwnerConfirmed: isConfirmed,
                isOwnerPending: !isConfirmed,
                canBook: false,
                canCancel,
                bookedTraining: training,
                displayTitle: isConfirmed ? t('yourBooking') : t('pending'),
            });
        });

        // Add locks
        trainingLocks.forEach((lock) => {
            eventsList.push({
                id: lock.id,
                slotId: `lock-${lock.id}`,
                startTime: lock.startTime,
                endTime: lock.endTime,
                type: 'locked',
                isLocked: true,
                canBook: false,
                displayTitle: t('unavailable'),
            });
        });

        // Add other confirmed trainings
        confirmedTrainings.forEach((ct) => {
            const isOwnerTraining = trainings.some((t) => t.id === ct.id);
            if (!isOwnerTraining) {
                eventsList.push({
                    id: ct.id,
                    slotId: `confirmed-${ct.id}`,
                    startTime: ct.startTime,
                    endTime: ct.endTime,
                    type: 'confirmed-other',
                    isConfirmedOther: true,
                    canBook: false,
                    displayTitle: t('occupied'),
                });
            }
        });

        // Add dog's group trainings (blue cards - owner's dog participates)
        dogGroupTrainings.forEach((gt) => {
            eventsList.push({
                id: gt.id,
                slotId: `dog-group-${gt.id}`,
                startTime: gt.startTime,
                endTime: gt.endTime,
                type: 'dog-group-training',
                isDogGroupTraining: true,
                canBook: false,
                displayTitle: t('groupTraining'),
                groupName: gt.groupName || gt.trainingGroup?.name,
                groupTraining: gt,
            });
        });

        // Add trainer's group trainings where dog is NOT participating (show as "Zajęte")
        trainerGroupTrainings.forEach((gt) => {
            // Check if this group training is already in dog's group trainings
            const isDogInGroup = dogGroupTrainings.some((dgt) => dgt.id === gt.id);
            if (!isDogInGroup) {
                eventsList.push({
                    id: gt.id,
                    slotId: `trainer-group-${gt.id}`,
                    startTime: gt.startTime,
                    endTime: gt.endTime,
                    type: 'trainer-group-training',
                    isTrainerGroupTraining: true,
                    canBook: false,
                    displayTitle: t('occupied'),
                });
            }
        });

        // Add pending slots from other users/dogs (show as "Zajęte" only when allowReserveList is false)
        // When allowReserveList is true, free slots will have a badge instead
        if (schoolSettings?.allowReserveList === false) {
            // Deduplicate by time range to avoid showing multiple "Zajęte" for the same slot
            const uniquePendingSlots = pendingOtherSlots.reduce((acc, ps) => {
                const key = `${ps.startTime}-${ps.endTime}`;
                if (!acc.has(key)) {
                    acc.set(key, ps);
                }
                return acc;
            }, new Map());

            uniquePendingSlots.forEach((ps, key) => {
                eventsList.push({
                    id: `pending-other-${key}`,
                    slotId: `pending-other-${key}`,
                    startTime: ps.startTime,
                    endTime: ps.endTime,
                    type: 'pending-other',
                    isPendingOther: true,
                    canBook: false,
                    displayTitle: t('occupied'),
                });
            });
        }

        // Generate free slots for the current week
        for (let i = 0; i < 7; i++) {
            const dayDate = currentWeekStart.add(i, 'day');
            const dayKey = DAY_MAPPING[dayDate.isoWeekday()];
            const dayWorkingHours = workingHours.find((wh) => wh.dayOfWeek === dayKey);

            if (!dayWorkingHours) continue;

            const [openHour, openMin] = dayWorkingHours.openingTime.split(':').map(Number);
            const [closeHour, closeMin] = dayWorkingHours.closingTime.split(':').map(Number);

            let slotTime = dayDate.hour(openHour).minute(openMin).second(0);
            const closeTime = dayDate.hour(closeHour).minute(closeMin).second(0);

            while (slotTime.add(serviceDurationMinutes, 'minute').isSameOrBefore(closeTime)) {
                const slotEnd = slotTime.add(serviceDurationMinutes, 'minute');
                const slotStartStr = slotTime.format('YYYY-MM-DDTHH:mm:ss');
                const slotEndStr = slotEnd.format('YYYY-MM-DDTHH:mm:ss');

                if (!isSlotOccupied(slotStartStr, slotEndStr)) {
                    const isPast = slotTime.isBefore(now);

                    // Check minBookingDaysInAdvance from school settings
                    const minDaysInAdvance = schoolSettings?.minBookingDaysInAdvance || 0;
                    const earliestBookableDate = now.add(minDaysInAdvance, 'day').startOf('day');
                    const isTooSoon = slotTime.isBefore(earliestBookableDate);

                    // Check if month is unlocked
                    const monthNames = [
                        'JANUARY', 'FEBRUARY', 'MARCH', 'APRIL', 'MAY', 'JUNE',
                        'JULY', 'AUGUST', 'SEPTEMBER', 'OCTOBER', 'NOVEMBER', 'DECEMBER'
                    ];
                    const slotMonth = monthNames[slotTime.month()];
                    const isMonthLocked = Array.isArray(schoolSettings?.unlockedMonths) &&
                        !schoolSettings.unlockedMonths.includes(slotMonth);

                    const canBook = !isPast && !isTooSoon && !isMonthLocked;
                    const isUnavailable = isPast || isTooSoon || isMonthLocked;

                    // Check for pending requests (for waiting list badge when allowReserveList is true)
                    const pendingCount = getPendingCountForSlot(slotStartStr, slotEndStr);
                    const hasWaitingList = schoolSettings?.allowReserveList === true && pendingCount > 0;

                    let displayTitle = t('freeSlot');
                    if (isPast) {
                        displayTitle = t('unavailable');
                    } else if (isTooSoon) {
                        displayTitle = t('unavailable');
                    } else if (isMonthLocked) {
                        displayTitle = t('monthLocked');
                    } else if (hasWaitingList) {
                        displayTitle = t('waitingList');
                    }

                    eventsList.push({
                        id: `free-${slotStartStr}`,
                        slotId: `free-${slotStartStr}`,
                        startTime: slotStartStr,
                        endTime: slotEndStr,
                        type: 'free',
                        canBook,
                        isPast,
                        isTooSoon,
                        isMonthLocked,
                        isUnavailable,
                        displayTitle,
                        pendingCount: hasWaitingList ? pendingCount : 0,
                        hasWaitingList,
                        badge: hasWaitingList ? `${pendingCount}` : null,
                    });
                }

                slotTime = slotTime.add(serviceDurationMinutes, 'minute');
            }
        }

        return eventsList;
    }, [selectedTrainer, workingHours, trainings, trainingLocks, confirmedTrainings, trainerGroupTrainings, dogGroupTrainings, pendingOtherSlots, serviceDurationMinutes, currentWeekStart, schoolSettings, t]);

    const getEventVariant = useCallback((event) => {
        if (event.isOwnerConfirmed) return 'confirmed';
        if (event.isOwnerPending) return 'pending';
        if (event.isDogGroupTraining) return 'group'; // Blue color for dog's group trainings
        if (event.isLocked) return 'locked';
        if (event.isConfirmedOther || event.isTrainerGroupTraining || event.isPendingOther) return 'occupied'; // Zajęte style
        if (event.isUnavailable || event.isPast) return 'unavailable';
        if (isSlotSelected(event.slotId)) return event.hasWaitingList ? 'waitlist-selected' : 'free-selected';
        if (event.hasWaitingList) return 'waitlist'; // Special style for waiting list slots
        return 'free';
    }, [isSlotSelected]);

    const handleEventClick = useCallback((event) => {
        if (event.type === 'free' && event.canBook) {
            toggleSlot({
                id: event.slotId,
                startTime: event.startTime,
                endTime: event.endTime,
                trainerId: selectedTrainer?.id,
                pendingCount: event.pendingCount || 0,
                hasWaitingList: event.hasWaitingList || false,
            });
        } else if (event.canCancel && event.bookedTraining) {
            onCancelTraining(event.bookedTraining);
        }
    }, [toggleSlot, selectedTrainer, onCancelTraining]);

    const handleBooking = useCallback(() => {
        if (selectedSlots.length > 0) {
            onBookSlots(selectedSlots);
            clearSelection();
        }
    }, [selectedSlots, onBookSlots, clearSelection]);

    const eventDays = useMemo(() => {
        return events
            .map((e) => dayjs(e.startTime).format('YYYY-MM-DD'))
            .filter((v, i, a) => a.indexOf(v) === i);
    }, [events]);

    const renderEvent = useCallback((event) => {
        const variant = getEventVariant(event);
        const isSelected = isSlotSelected(event.slotId);

        return (
            <CalendarEvent
                key={event.id || event.slotId}
                event={event}
                variant={variant}
                position={event.position}
                overlap={event.overlap}
                selectable={event.type === 'free' && event.canBook}
                selected={isSelected}
                onSelect={() => handleEventClick(event)}
                onClick={() => handleEventClick(event)}
            />
        );
    }, [getEventVariant, isSlotSelected, handleEventClick]);

    if (shouldUseMobileView()) {
        return (
            <>
                <OwnerCalendarControls
                    trainers={trainers}
                    ownerDogs={ownerDogs}
                    services={services}
                    selectedTrainerId={selectedTrainer?.id}
                    selectedDogId={selectedDogId}
                    selectedServiceId={selectedServiceId}
                    onTrainerChange={onTrainerChange}
                    onDogChange={onDogChange}
                    onServiceChange={onServiceChange}
                    onMultipleBookingClick={onToggleBookingMode}
                    bookingMode={bookingMode}
                />

                {isReady ? (
                    <>
                        <Calendar.MobileNavigation />
                        <Calendar.MobileWeekStrip
                            selectedDay={selectedDay}
                            onDaySelect={setSelectedDay}
                            eventDays={eventDays}
                        />
                        <Calendar.MobileDayView
                            selectedDay={selectedDay}
                            events={events}
                            onEventClick={handleEventClick}
                        />
                    </>
                ) : (
                    <SelectionPlaceholder isMobileView={true} />
                )}

                <BookingBar
                    selectedSlots={selectedSlots}
                    onClearSelection={clearSelection}
                    onBookSlots={handleBooking}
                    isBooking={isBooking}
                />
            </>
        );
    }

    return (
        <>
            <OwnerCalendarControls
                trainers={trainers}
                ownerDogs={ownerDogs}
                services={services}
                selectedTrainerId={selectedTrainer?.id}
                selectedDogId={selectedDogId}
                selectedServiceId={selectedServiceId}
                onTrainerChange={onTrainerChange}
                onDogChange={onDogChange}
                onServiceChange={onServiceChange}
                onMultipleBookingClick={onToggleBookingMode}
                bookingMode={bookingMode}
            />

            {isReady ? (
                <>
                    <Calendar.Navigation />
                    <Calendar.Grid
                        events={events}
                        renderEvent={renderEvent}
                        onEventClick={handleEventClick}
                    />
                </>
            ) : (
                <SelectionPlaceholder />
            )}

            <BookingBar
                selectedSlots={selectedSlots}
                onClearSelection={clearSelection}
                onBookSlots={handleBooking}
                isBooking={isBooking}
            />
        </>
    );
};

const OwnerCalendar = () => {
    const { t } = useTranslation('calendar');
    const { owner } = useContext(OwnerContext);
    const [searchParams] = useSearchParams();

    const [trainers, setTrainers] = useState([]);
    const [ownerDogs, setOwnerDogs] = useState([]);
    const [services, setServices] = useState([]);
    const [workingHours, setWorkingHours] = useState([]);
    const [schoolSettings, setSchoolSettings] = useState(null);

    const [selectedTrainer, setSelectedTrainer] = useState(null);
    const [selectedDogId, setSelectedDogId] = useState(null);
    const [selectedServiceId, setSelectedServiceId] = useState(null);

    const [trainings, setTrainings] = useState([]);
    const [confirmedTrainings, setConfirmedTrainings] = useState([]);
    const [trainingLocks, setTrainingLocks] = useState([]);
    const [trainerGroupTrainings, setTrainerGroupTrainings] = useState([]);
    const [dogGroupTrainings, setDogGroupTrainings] = useState([]); // Group trainings where owner's dog participates
    const [pendingOtherSlots, setPendingOtherSlots] = useState([]); // Pending requests from other users/dogs

    const [bookingMode, setBookingMode] = useState('single');
    const [isBooking, setIsBooking] = useState(false);
    const [isLoading, setIsLoading] = useState(false);

    const [additionalInfoModalOpen, setAdditionalInfoModalOpen] = useState(false);
    const [deleteModalOpen, setDeleteModalOpen] = useState(false);
    const [trainingToDelete, setTrainingToDelete] = useState(null);
    const [pendingBookingSlots, setPendingBookingSlots] = useState([]);

    // Track current viewed week for refresh after booking
    const [currentViewedWeek, setCurrentViewedWeek] = useState(dayjs().startOf('isoWeek'));

    const selectedService = useMemo(() => {
        return services.find((s) => s.id === selectedServiceId);
    }, [services, selectedServiceId]);

    const parseDurationToMinutes = (duration) => {
        if (!duration) return 60;
        if (typeof duration === 'number') return duration;

        const match = duration.match(/PT(?:(\d+)H)?(?:(\d+)M)?/);
        if (!match) return 60;

        const hours = parseInt(match[1] || '0', 10);
        const minutes = parseInt(match[2] || '0', 10);
        return hours * 60 + minutes;
    };

    const serviceDurationMinutes = useMemo(() => {
        if (!selectedService) return 60;
        return parseDurationToMinutes(selectedService.calendarDuration) || 60;
    }, [selectedService]);

    const getAuthHeader = useCallback(() => ({
        Authorization: `Bearer ${localStorage.getItem('jwt')}`,
    }), []);

    const fetchTrainers = useCallback(async () => {
        if (!owner?.schoolId) return;

        try {
            const response = await axios.get(`/api/v1/trainer/find-all/${owner.schoolId}`, {
                headers: getAuthHeader(),
            });
            setTrainers(response.data || []);
        } catch (error) {
            console.error('Failed to fetch trainers:', error);
        }
    }, [owner?.schoolId, getAuthHeader]);

    const fetchOwnerDogs = useCallback(async () => {
        if (!owner?.id) return;

        try {
            const response = await axios.get(`/api/v1/dog/${owner.id}/list`, {
                headers: getAuthHeader(),
            });
            setOwnerDogs((response.data || []).filter(d => !d.pendingApproval));
        } catch (error) {
            console.error('Failed to fetch dogs:', error);
        }
    }, [owner?.id, getAuthHeader]);

    const fetchServices = useCallback(async () => {
        if (!owner?.schoolId) return;

        try {
            const response = await axios.get(`/api/v1/services/school/${owner.schoolId}`, {
                headers: getAuthHeader(),
            });
            setServices(response.data || []);
        } catch (error) {
            console.error('Failed to fetch services:', error);
        }
    }, [owner?.schoolId, getAuthHeader]);

    const fetchSchoolSettings = useCallback(async () => {
        if (!owner?.schoolId) return;

        try {
            const response = await axios.get(`/api/v1/school/${owner.schoolId}/settings`, {
                headers: getAuthHeader(),
            });
            setSchoolSettings(response.data);
        } catch (error) {
            console.error('Failed to fetch school settings:', error);
        }
    }, [owner?.schoolId, getAuthHeader]);

    const fetchWorkingHours = useCallback(async () => {
        if (!selectedTrainer?.id) return;

        try {
            const response = await axios.get(`/api/v1/trainer/get-working-hours/${selectedTrainer.id}`, {
                headers: getAuthHeader(),
            });
            setWorkingHours(response.data || []);
        } catch (error) {
            console.error('Failed to fetch working hours:', error);
        }
    }, [selectedTrainer?.id, getAuthHeader]);

    const fetchCalendarData = useCallback(async (weekStart) => {
        if (!selectedTrainer?.id || !selectedDogId) return;

        setIsLoading(true);
        setCurrentViewedWeek(weekStart);

        const startTime = weekStart.startOf('isoWeek').format('YYYY-MM-DDTHH:mm:ss');
        const endTime = weekStart.endOf('isoWeek').format('YYYY-MM-DDTHH:mm:ss');

        try {
            // Build base requests
            const requests = [
                // Owner's trainings for this dog with this trainer
                axios.get(`/api/v1/training/getAllTrainingsForDogForTrainer/${selectedDogId}/${selectedTrainer.id}`, {
                    params: { startTime, endTime },
                    headers: getAuthHeader(),
                }),
                // Confirmed trainings for this trainer/dog combination (to show as "occupied")
                axios.get(`/api/v1/training/${selectedTrainer.id}/${selectedDogId}/getConfirmedTrainingsForTrainer`, {
                    params: { startTime, endTime },
                    headers: getAuthHeader(),
                }),
                // Trainer's locked slots
                axios.get(`/api/v1/training/lock/${selectedTrainer.id}`, {
                    params: { startTime, endTime },
                    headers: getAuthHeader(),
                }),
                // Trainer's group trainings (to block those slots as "Zajęte")
                axios.get(`/api/v1/group-trainings/trainer/${selectedTrainer.id}`, {
                    params: { startTime, endTime },
                    headers: getAuthHeader(),
                }),
                // Dog's group trainings (to show as blue cards - owner's dog participates)
                axios.get(`/api/v1/group-trainings/dog/${selectedDogId}`, {
                    params: { startTime, endTime },
                    headers: getAuthHeader(),
                }),
            ];

            // Always fetch pending slots to show waiting list info
            requests.push(
                axios.get(`/api/v1/training/pending-slots/${selectedTrainer.id}`, {
                    params: { excludeDogId: selectedDogId, startTime, endTime },
                    headers: getAuthHeader(),
                })
            );

            const responses = await Promise.all(requests);

            const [trainingsRes, confirmedRes, locksRes, trainerGroupRes, dogGroupRes, pendingSlotsRes] = responses;

            setTrainings(trainingsRes.data || []);
            setConfirmedTrainings(confirmedRes.data || []);
            setTrainingLocks(locksRes.data || []);

            // Transform trainer's group trainings to have startTime/endTime for compatibility
            const transformedTrainerGroupTrainings = (trainerGroupRes.data || []).map(gt => ({
                ...gt,
                startTime: gt.scheduledAt,
                endTime: dayjs(gt.scheduledAt).add(gt.durationMinutes || 60, 'minute').toISOString(),
            }));
            setTrainerGroupTrainings(transformedTrainerGroupTrainings);

            // Transform dog's group trainings
            const transformedDogGroupTrainings = (dogGroupRes.data || []).map(gt => ({
                ...gt,
                startTime: gt.scheduledAt,
                endTime: dayjs(gt.scheduledAt).add(gt.durationMinutes || 60, 'minute').toISOString(),
            }));
            setDogGroupTrainings(transformedDogGroupTrainings);

            // Set pending slots from other users/dogs
            setPendingOtherSlots(pendingSlotsRes?.data || []);
        } catch (error) {
            console.error('Failed to fetch calendar data:', error);
        } finally {
            setIsLoading(false);
        }
    }, [selectedTrainer?.id, selectedDogId, getAuthHeader]);

    useEffect(() => {
        fetchTrainers();
        fetchOwnerDogs();
        fetchServices();
        fetchSchoolSettings();
    }, [fetchTrainers, fetchOwnerDogs, fetchServices, fetchSchoolSettings]);

    useEffect(() => {
        fetchWorkingHours();
    }, [fetchWorkingHours]);

    useEffect(() => {
        const trainerIdParam = searchParams.get('trainerId');
        if (trainerIdParam && trainers.length > 0) {
            const trainer = trainers.find((t) => t.id === trainerIdParam);
            if (trainer) {
                setSelectedTrainer(trainer);
            }
        }
    }, [searchParams, trainers]);

    const clearCalendarData = useCallback(() => {
        setTrainings([]);
        setConfirmedTrainings([]);
        setTrainingLocks([]);
        setTrainerGroupTrainings([]);
        setDogGroupTrainings([]);
        setPendingOtherSlots([]);
    }, []);

    const handleTrainerChange = useCallback((trainerId) => {
        const trainer = trainers.find((t) => t.id === trainerId);
        setSelectedTrainer(trainer || null);
        clearCalendarData();
    }, [trainers, clearCalendarData]);

    const handleDogChange = useCallback((dogId) => {
        setSelectedDogId(dogId || null);
        clearCalendarData();
    }, [clearCalendarData]);

    const handleServiceChange = useCallback((serviceId) => {
        setSelectedServiceId(serviceId || null);
    }, []);

    const handleToggleBookingMode = useCallback(() => {
        setBookingMode((prev) => (prev === 'single' ? 'multiple' : 'single'));
    }, []);

    const handleBookSlots = useCallback((slots) => {
        setPendingBookingSlots(slots);
        setAdditionalInfoModalOpen(true);
    }, []);

    const handleConfirmBooking = useCallback(async (additionalInfo = '') => {
        if (!pendingBookingSlots.length || !selectedTrainer?.id || !selectedDogId) return;

        setIsBooking(true);
        setAdditionalInfoModalOpen(false);

        try {
            if (pendingBookingSlots.length === 1) {
                const slot = pendingBookingSlots[0];
                await axios.post('/api/v1/training/requestBookingTraining', {
                    trainerId: selectedTrainer.id,
                    dogId: selectedDogId,
                    hallId: null,
                    serviceId: selectedServiceId,
                    additionalInfo,
                    startTime: slot.startTime,
                    endTime: slot.endTime || dayjs(slot.startTime).add(serviceDurationMinutes, 'minute').format('YYYY-MM-DDTHH:mm:ss'),
                }, {
                    headers: getAuthHeader(),
                });
            } else {
                const requests = pendingBookingSlots.map((slot) => ({
                    trainerId: selectedTrainer.id,
                    dogId: selectedDogId,
                    hallId: null,
                    serviceId: selectedServiceId,
                    startTime: slot.startTime,
                    endTime: slot.endTime || dayjs(slot.startTime).add(serviceDurationMinutes, 'minute').format('YYYY-MM-DDTHH:mm:ss'),
                    additionalInfo,
                }));

                await axios.post('/api/v1/training/requestMultipleBookingTraining', requests, {
                    headers: getAuthHeader(),
                });
            }

            setPendingBookingSlots([]);
            // Refresh the currently viewed week
            fetchCalendarData(currentViewedWeek);
        } catch (error) {
            console.error('Failed to book training:', error);
        } finally {
            setIsBooking(false);
        }
    }, [pendingBookingSlots, selectedTrainer, selectedDogId, selectedServiceId, serviceDurationMinutes, getAuthHeader, fetchCalendarData, currentViewedWeek]);

    const handleCancelTraining = useCallback((training) => {
        setTrainingToDelete(training);
        setDeleteModalOpen(true);
    }, []);

    const handleConfirmCancel = useCallback(async () => {
        if (!trainingToDelete) return;

        try {
            await axios.post(`/api/v1/training/${trainingToDelete.id}/decline`, {}, {
                headers: getAuthHeader(),
            });

            setDeleteModalOpen(false);
            setTrainingToDelete(null);
            // Refresh the currently viewed week
            fetchCalendarData(currentViewedWeek);
        } catch (error) {
            console.error('Failed to cancel training:', error);
        }
    }, [trainingToDelete, getAuthHeader, fetchCalendarData, currentViewedWeek]);

    return (
        <OwnerLayout>
            <div className="owner-calendar-container">
                <div className="owner-calendar-header">
                    <h1 className="owner-calendar-title">{t('bookTrainings')}</h1>
                </div>

                <Calendar
                    trainerId={selectedTrainer?.id}
                    schoolId={owner?.schoolId}
                    workingHours={workingHours}
                    basePath="/owner/calendar"
                    multiSelect={schoolSettings?.allowMultipleBookings ?? false}
                    maxSelections={20}
                    features={{
                        slotSelection: true,
                        showCurrentTime: true,
                    }}
                >
                    <OwnerCalendarContent
                        trainings={trainings}
                        trainingLocks={trainingLocks}
                        confirmedTrainings={confirmedTrainings}
                        trainerGroupTrainings={trainerGroupTrainings}
                        dogGroupTrainings={dogGroupTrainings}
                        pendingOtherSlots={pendingOtherSlots}
                        trainers={trainers}
                        ownerDogs={ownerDogs}
                        services={services}
                        workingHours={workingHours}
                        schoolSettings={schoolSettings}
                        selectedTrainer={selectedTrainer}
                        selectedDogId={selectedDogId}
                        selectedServiceId={selectedServiceId}
                        serviceDurationMinutes={serviceDurationMinutes}
                        onTrainerChange={handleTrainerChange}
                        onDogChange={handleDogChange}
                        onServiceChange={handleServiceChange}
                        onBookSlots={handleBookSlots}
                        onCancelTraining={handleCancelTraining}
                        onWeekChange={fetchCalendarData}
                        isBooking={isBooking}
                        bookingMode={bookingMode}
                        onToggleBookingMode={handleToggleBookingMode}
                    />
                </Calendar>

                {additionalInfoModalOpen && (
                    <AdditionalInfoModal
                        isOpen={additionalInfoModalOpen}
                        onClose={() => setAdditionalInfoModalOpen(false)}
                        onConfirm={handleConfirmBooking}
                        slotsCount={pendingBookingSlots.length}
                        waitingListSlots={pendingBookingSlots.filter(slot => slot.hasWaitingList)}
                        totalPendingCount={pendingBookingSlots.reduce((sum, slot) => sum + (slot.pendingCount || 0), 0)}
                    />
                )}

                {deleteModalOpen && (
                    <DeleteConfirmationModal
                        isOpen={deleteModalOpen}
                        onClose={() => {
                            setDeleteModalOpen(false);
                            setTrainingToDelete(null);
                        }}
                        onConfirm={handleConfirmCancel}
                        title={t('cancelTraining')}
                        message={t('cancelTrainingConfirm')}
                    />
                )}
            </div>
        </OwnerLayout>
    );
};

export default OwnerCalendar;